<template>
    <q-page padding class="usuarios-page">
        <!-- Header moderno mejorado -->
        <div class="page-header">
            <div class="row items-center no-wrap">
                <q-avatar size="56px" color="primary" text-color="white">
                    <q-icon name="group" size="32px" />
                </q-avatar>
                <div class="col q-ml-md">
                    <div class="text-h4 text-weight-bold text-grey-9">Gestión de Usuarios</div>
                    <div class="text-subtitle2 text-grey-6">
                        Administre usuarios, estados y permisos del sistema
                    </div>
                </div>
                <q-chip
                    color="primary"
                    text-color="white"
                    icon="people"
                    class="chip-info"
                >
                    {{ pagination.rowsNumber }} usuarios
                </q-chip>
            </div>
        </div>

        <!-- Tarjeta de tabla mejorada -->
        <q-card class="table-card q-mt-lg">
            <q-card-section class="card-header">
                <div class="row items-center no-wrap">
                    <q-icon name="table_chart" size="28px" class="q-mr-sm" />
                    <div class="col">
                        <div class="text-h6 text-weight-bold">Listado de Usuarios</div>
                        <div class="text-caption text-white-7 q-mt-xs">
                            Gestione y visualice todos los usuarios registrados
                        </div>
                    </div>
                </div>
            </q-card-section>

            <q-separator />

            <q-card-section class="q-pa-none">
                <q-table
                    ref="tableRef"
                    :rows="rows"
                    :columns="columns"
                    row-key="id"
                    v-model:pagination="pagination"
                    :loading="loading"
                    :filter="filter"
                    @request="onRequest"
                    flat
                    class="modern-table"
                >
                    <template v-slot:top>
                        <div class="full-width row q-gutter-md q-pa-md">
                            <q-btn
                                unelevated
                                color="positive"
                                icon="add"
                                label="Nuevo Usuario"
                                @click="usuarioAdd"
                                class="action-btn"
                                no-caps
                            />
                            <q-space />
                            <q-input
                                outlined
                                dense
                                debounce="500"
                                v-model="filter"
                                placeholder="Buscar usuarios..."
                                style="min-width: 300px"
                                class="search-input"
                            >
                                <template v-slot:prepend>
                                    <q-icon name="search" color="primary" />
                                </template>
                                <template v-slot:append v-if="filter">
                                    <q-icon
                                        name="close"
                                        @click="filter = ''"
                                        class="cursor-pointer"
                                    />
                                </template>
                            </q-input>
                        </div>
                    </template>

                    <template v-slot:body-cell-name="props">
                        <q-td :props="props">
                            <div class="row items-center no-wrap">
                                <q-avatar size="40px" color="blue-1" text-color="blue-9" class="q-mr-sm">
                                    <q-img
                                        :src="`https://api.dicebear.com/7.x/initials/svg?seed=${props.row.name}`"
                                        :alt="props.row.name"
                                    />
                                </q-avatar>
                                <div>
                                    <div class="text-weight-bold text-grey-9">{{ props.row.name }}</div>
                                </div>
                            </div>
                        </q-td>
                    </template>

                    <template v-slot:body-cell-email="props">
                        <q-td :props="props">
                            <div class="row items-center">
                                <q-icon name="email" size="18px" color="grey-6" class="q-mr-xs" />
                                <span class="text-grey-8">{{ props.row.email }}</span>
                            </div>
                        </q-td>
                    </template>

                    <template v-slot:body-cell-vendedor="props">
                        <q-td :props="props">
                            <div v-if="props.row.A3_NOME" class="row items-center">
                                <q-icon name="badge" size="18px" color="purple-6" class="q-mr-xs" />
                                <span class="text-grey-8">{{ props.row.A3_NOME }}</span>
                            </div>
                            <span v-else class="text-grey-5">—</span>
                        </q-td>
                    </template>

                    <template v-slot:body-cell-fuerza_venta="props">
                        <q-td :props="props">
                            <q-badge
                                v-if="props.row.A3_XFZAVEN"
                                color="purple-2"
                                text-color="purple-9"
                                :label="props.row.A3_XFZAVEN"
                                class="q-px-md q-py-xs"
                            />
                            <span v-else class="text-grey-5">—</span>
                        </q-td>
                    </template>

                    <template v-slot:body-cell-active="props">
                        <q-td :props="props">
                            <q-btn
                                :color="props.row.is_active ? 'positive' : 'negative'"
                                :loading="loadingStatus === props.row.id"
                                round
                                unelevated
                                :icon="props.row.is_active ? 'check_circle' : 'cancel'"
                                size="sm"
                                @click="setStatus(props.row.id)"
                            >
                                <q-tooltip>
                                    {{ props.row.is_active ? 'Usuario activo - Clic para desactivar' : 'Usuario inactivo - Clic para activar' }}
                                </q-tooltip>
                            </q-btn>
                        </q-td>
                    </template>

                    <template v-slot:body-cell-acciones="props">
                        <q-td :props="props">
                            <q-btn
                                color="primary"
                                :loading="editLoading === props.row.id"
                                round
                                unelevated
                                icon="edit"
                                size="sm"
                                @click="edit(props.row)"
                            >
                                <q-tooltip>Editar usuario</q-tooltip>
                            </q-btn>
                        </q-td>
                    </template>

                    <template v-slot:loading>
                        <q-inner-loading showing color="primary">
                            <q-spinner-hourglass size="50px" />
                        </q-inner-loading>
                    </template>
                </q-table>
            </q-card-section>
        </q-card>
    </q-page>

    <UsuarioCrud
        v-model="usuarioCrudOpen"
        :oper="usuarioCrudOper"
        :formulario="usuarioData"
        @updated="tableRefresh"
    />
</template>
<script setup>
import UserService from "@/services/UserService.js";
import UsuarioCrud from "./Usuarios/UsuarioCrud.vue";
import { ref, onMounted } from "vue";


const filter = ref("");
const tableRef = ref();
const loading = ref(false);
const loadingStatus = ref(null);
const pagination = ref({
    sortBy: "id",
    descending: true,
    page: 1,
    rowsPerPage: 100,
    rowsNumber: 10,
    cliente: true,
});
const columns = [
    {
        name: "name",
        label: "Nombre",
        field: "name",
        align: "left",
        sortable: true,
    },
    {
        label: "Email",
        name: "email",
        align: "left",
        field: "email",
        sortable: true,
    },
    {
        label: "Vendedor",
        name: "vendedor",
        align: "left",
        field: "A3_NOME",
        sortable: true,
    },
    {
        label: "Fuerza de venta",
        name: "fuerza_venta",
        align: "left",
        field: "A3_XFZAVEN",
        sortable: true,
    },
    {
        label: "Activo",
        name: "active",
        align: "center",
        field: "is_active",
        sortable: true,
    },
    {
        label: "Acciones",
        name: "acciones",
        align: "center",
    },
];
const rows = ref([]);
const onRequest = async (props) => {
    const filter = props.filter;
    const { page, rowsPerPage, sortBy, descending } = props.pagination;
    loading.value = true;

    const { data, per_page, current_page, total } = await UserService.index({
        page,
        rowsPerPage,
        sortBy,
        descending,
        filter,
    });
    rows.value = data;
    console.log(data);
    pagination.value.rowsPerPage = per_page;
    pagination.value.page = current_page;
    pagination.value.rowsNumber = total;
    pagination.value.sortBy = sortBy;
    pagination.value.descending = descending;
    loading.value = false;
};
onMounted(() => {
    // get initial data from server (1st page)
    tableRef.value.requestServerInteraction();
});
const tableRefresh = () => tableRef.value.requestServerInteraction();

const editLoading = ref(false);
const usuarioCrudOpen = ref(false);
const usuarioCrudOper = ref("edit");
const usuarioData = ref({});
const usuarioId = ref(null);
const usuarioAdd = () => {
    usuarioCrudOper.value = "add";
    usuarioCrudOpen.value = true;
    usuarioId.value = null;
};
const edit = async (formulario) => {
    usuarioData.value = formulario;
    usuarioCrudOper.value = "edit";
    usuarioCrudOpen.value = true;
    editLoading.value = false;
};
const setStatus = async (id) => {
    loadingStatus.value = id;
    await UserService.setStatus(id);
    tableRefresh();
    loadingStatus.value = null;
};
</script>

<style lang="scss" scoped>
@use 'sass:color';

.usuarios-page {
    background: linear-gradient(to bottom, #fafafa 0%, #f5f5f5 100%);
    min-height: 100vh;
}

.page-header {
    background: white;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    animation: fadeInDown 0.5s ease;
    transition: all 0.3s ease;

    &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
}

.chip-info {
    font-size: 14px;
    padding: 8px 16px;
    font-weight: 600;
}

.table-card {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    transition: all 0.3s ease;
    animation: fadeIn 0.4s ease 0.2s backwards;

    &:hover {
        box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
    }
}

.card-header {
    background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
    color: white;
    padding: 24px;
}

.text-white-7 {
    color: rgba(255, 255, 255, 0.85);
}

// Tabla moderna
.modern-table {
    :deep(.q-table__top) {
        background: #fafafa;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
    }

    :deep(thead tr th) {
        background: linear-gradient(to bottom, #f5f5f5 0%, #eeeeee 100%);
        color: #424242;
        font-weight: 700;
        font-size: 13px;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        position: sticky;
        top: 0;
        z-index: 1;
        border-bottom: 2px solid #1976d2;
    }

    :deep(tbody tr) {
        transition: all 0.2s ease;

        &:hover {
            background: linear-gradient(to right, #e3f2fd 0%, #fafafa 100%);
            transform: translateX(4px);
        }
    }

    :deep(tbody td) {
        padding: 12px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.05);
    }

    :deep(.q-table__bottom) {
        background: #fafafa;
        border-top: 1px solid rgba(0, 0, 0, 0.08);
    }
}

// Input de búsqueda mejorado
.search-input {
    :deep(.q-field__control) {
        background: white;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
    }

    :deep(.q-field__native) {
        font-weight: 500;
    }
}

// Botón de acción
.action-btn {
    font-weight: 600;
    padding: 10px 20px;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    }
}

// Mejoras de botones en tabla
:deep(.q-btn) {
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
        transform: translateY(-2px);
    }

    &.q-btn--round {
        &:hover:not(:disabled) {
            transform: scale(1.1);
        }
    }
}

// Badges mejorados
:deep(.q-badge) {
    font-weight: 600;
    border-radius: 6px;
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.05);
    }
}

// Avatares con efecto
:deep(.q-avatar) {
    transition: all 0.2s ease;

    &:hover {
        transform: scale(1.1);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
}

// Animaciones
@keyframes fadeInDown {
    from {
        opacity: 0;
        transform: translateY(-30px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

// Responsive
@media (max-width: 1023px) {
    .page-header {
        border-radius: 12px;
        padding: 20px;

        .text-h4 {
            font-size: 1.5rem;
        }

        .chip-info {
            display: none;
        }
    }

    .table-card {
        border-radius: 12px;
    }

    .search-input {
        min-width: 200px !important;
    }

    .modern-table {
        :deep(.q-table) {
            font-size: 12px;
        }

        :deep(thead tr th) {
            font-size: 11px;
        }
    }
}

@media (max-width: 599px) {
    .page-header {
        .row {
            flex-wrap: wrap;
        }

        .q-avatar {
            size: 40px;
        }

        .text-h4 {
            font-size: 1.25rem;
        }
    }

    .search-input {
        min-width: 100% !important;
    }
}
</style>
